import { useMemo, useState } from "react";
import {
  List,
  LayoutGrid,
  MousePointerClick,
  SearchX,
  SlidersHorizontal,
  ChevronDown,
  Bookmark,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ItemRow } from "@/components/ItemRow";
import { ItemCard } from "@/components/ItemCard";
import { DetailPane } from "@/components/DetailPane";
import { EmptyState } from "@/components/EmptyState";
import { CaptureButton, HeaderSearch } from "@/components/PageHeader";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { useIsMobile } from "@/hooks/use-mobile";
import { tags } from "@/lib/data/tags";
import { TOTAL_SAVED_BASE, INITIAL_ITEM_COUNT } from "@/lib/data/items";
import { STATUS_META } from "@/components/StatusBadge";
import { dateGroup, GROUP_ORDER } from "@/lib/format";

const PLATFORMS = ["YouTube", "TikTok", "X", "Web"];
const STATUSES = ["inbox", "pendiente", "importante", "aplicado", "archivado"];

const FilterChip = ({ label, count, onClick, children, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        data-testid={testId}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] whitespace-nowrap transition-colors shrink-0",
          count ? "text-foreground border-primary/40" : "text-muted-foreground hover:text-foreground hover:bg-secondary",
        )}
      >
        {label}
        {count > 0 && (
          <span className="rounded-full bg-accent text-accent-foreground px-1.5 text-[11px] font-semibold tabular-nums">{count}</span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-52 max-h-72 overflow-y-auto">
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);

export default function Biblioteca() {
  const { items, selectedId, select, detailOpen, closeDetail, booted } = useItemsStore();
  const isMobile = useIsMobile();
  const [view, setView] = useState("list");
  const [platformF, setPlatformF] = useState([]);
  const [tagF, setTagF] = useState([]);
  const [statusF, setStatusF] = useState([]);

  const toggleIn = (setter) => (value) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const filtered = useMemo(() => {
    let list = items;
    if (platformF.length) list = list.filter((i) => platformF.includes(i.platform));
    if (tagF.length) list = list.filter((i) => i.tags.some((t) => tagF.includes(t)));
    if (statusF.length) list = list.filter((i) => statusF.includes(i.status));
    return [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [items, platformF, tagF, statusF]);

  const grouped = useMemo(() => {
    const g = {};
    for (const item of filtered) {
      const key = dateGroup(item.createdAt);
      (g[key] = g[key] || []).push(item);
    }
    return GROUP_ORDER.filter((k) => g[k]?.length).map((k) => [k, g[k]]);
  }, [filtered]);

  const selected = items.find((i) => i.id === selectedId);
  const total = TOTAL_SAVED_BASE + items.length - INITIAL_ITEM_COUNT;
  const activeFilters = platformF.length + tagF.length + statusF.length;

  const header = (
    <div className="pt-5 pb-3 shrink-0 border-b">
      <div className="max-w-[1400px] mx-auto w-full px-4 md:px-8 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-serif text-2xl md:text-3xl tracking-tight">Biblioteca</h1>
        <div className="flex items-center gap-2">
          <HeaderSearch />
          <CaptureButton />
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin" data-testid="filter-toolbar">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] shrink-0",
            activeFilters ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
          Filtros
          {activeFilters > 0 && (
            <span className="rounded-full bg-primary text-primary-foreground px-1.5 text-[11px] font-semibold tabular-nums">
              {activeFilters}
            </span>
          )}
        </span>

        <FilterChip label="Plataforma" count={platformF.length} testId="filter-platform">
          {PLATFORMS.map((p) => (
            <DropdownMenuCheckboxItem key={p} checked={platformF.includes(p)} onCheckedChange={() => toggleIn(setPlatformF)(p)}>
              {p}
            </DropdownMenuCheckboxItem>
          ))}
        </FilterChip>

        <FilterChip label="Etiquetas" count={tagF.length} testId="filter-tags">
          {tags.map((t) => (
            <DropdownMenuCheckboxItem key={t.id} checked={tagF.includes(t.name)} onCheckedChange={() => toggleIn(setTagF)(t.name)}>
              #{t.name} <span className="ml-auto text-xs text-muted-foreground">{t.count}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </FilterChip>

        <FilterChip label="Estado" count={statusF.length} testId="filter-status">
          {STATUSES.map((s) => (
            <DropdownMenuCheckboxItem key={s} checked={statusF.includes(s)} onCheckedChange={() => toggleIn(setStatusF)(s)}>
              {STATUS_META[s].label}
            </DropdownMenuCheckboxItem>
          ))}
        </FilterChip>

        <button
          type="button"
          onClick={() => toast("Filtro por fecha en la versión final.")}
          className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground shrink-0"
        >
          Fecha <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => toast("Más filtros en la versión final.")}
          className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground shrink-0"
        >
          Más filtros
        </button>

        <span className="flex-1" />

        <button
          type="button"
          onClick={() => toast("Podrás guardar filtros en la versión final.")}
          className="hidden lg:inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline shrink-0"
        >
          <Bookmark className="h-3.5 w-3.5" strokeWidth={1.75} /> Guardar filtro
        </button>
        <button
          type="button"
          onClick={() => toast("Orden configurable en la versión final.")}
          className="hidden lg:inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground shrink-0"
        >
          <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Orden: Recientes
          <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
        </button>
        <span className="text-[13px] text-muted-foreground tabular-nums shrink-0" data-testid="library-count">
          {total.toLocaleString("es-ES")} contenidos
        </span>

        <div className="flex rounded-md border overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => setView("list")}
            aria-label="Vista de lista"
            data-testid="view-toggle-list"
            className={cn("px-2 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition-colors", view === "list" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary")}
          >
            <List className="h-3.5 w-3.5" strokeWidth={1.75} /> <span className="hidden xl:inline">Lista</span>
          </button>
          <button
            type="button"
            onClick={() => setView("grid")}
            aria-label="Vista de rejilla"
            data-testid="view-toggle-grid"
            className={cn("px-2 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition-colors border-l", view === "grid" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary")}
          >
            <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} /> <span className="hidden xl:inline">Rejilla</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );

  const emptyFiltered = (
    <EmptyState
      icon={SearchX}
      title="Nada por aquí con este filtro."
      hint="Prueba con otra plataforma o etiqueta, o comparte contenido nuevo."
      testId="library-empty"
    />
  );

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="library-page">
      {header}

      {view === "list" ? (
        <div className="flex-1 flex min-h-0 max-w-[1400px] mx-auto w-full md:px-8">
          <div className="w-full md:w-[400px] lg:w-[440px] md:border-r flex flex-col min-h-0 shrink-0">
            <div className="flex-1 overflow-y-auto scrollbar-thin pb-4">
              {!booted ? (
                <div className="px-4 pt-4 space-y-2">
                  {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} className="h-[84px] rounded-lg" />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                emptyFiltered
              ) : (
                grouped.map(([groupName, groupItems]) => (
                  <div key={groupName}>
                    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur px-4 pt-4 pb-1.5 flex items-center gap-2">
                      <p className="text-sm font-semibold">{groupName}</p>
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground tabular-nums">
                        {groupItems.length}
                      </span>
                    </div>
                    <div className="px-2 space-y-0.5">
                      {groupItems.map((item) => (
                        <ItemRow key={item.id} item={item} selected={item.id === selectedId} onClick={() => select(item.id)} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="hidden md:flex flex-1 min-w-0 min-h-0">
            {selected ? (
              <div className="flex-1 min-w-0">
                <DetailPane item={selected} onClose={() => select(null)} />
              </div>
            ) : (
              <div className="flex-1 grid place-items-center" data-testid="detail-empty">
                <EmptyState
                  icon={MousePointerClick}
                  title="Selecciona un contenido para ver su detalle"
                  hint="El resumen de IA, las etiquetas y las acciones aparecerán aquí."
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 md:px-8 py-5">
          {!booted ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-72 rounded-lg" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            emptyFiltered
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 max-w-[1400px] mx-auto">
              {filtered.map((item, idx) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  featured={idx === 0 && activeFilters === 0}
                  selected={item.id === selectedId}
                  onClick={() => {
                    select(item.id);
                    if (window.matchMedia("(min-width: 768px)").matches) setView("list");
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {isMobile && selected && detailOpen && (
        <div className="fixed inset-0 z-50 bg-background md:hidden" data-testid="mobile-detail-overlay">
          <DetailPane item={selected} onBack={closeDetail} />
        </div>
      )}
    </div>
  );
}
